import { MoneyFieldsFragment } from "@repo/graphql/documents/generic/fragment.generated";
import { MoneyInput } from "@repo/graphql/gql/graphql";

import { CurrencyCode, isCurrencyCode } from "./currencies";

export const CURRENCY_LOCALES: Record<CurrencyCode, string> = {
  CLP: "es-CL",
  PEN: "es-PE",
  ARS: "es-AR",
  COP: "es-CO",
  USD: "en-US",
  BRL: "pt-BR",
};

export const CURRENCY_SYMBOLS: Record<CurrencyCode, string> = {
  CLP: "$",
  PEN: "S/",
  ARS: "$",
  COP: "$",
  USD: "US$",
  BRL: "R$",
};

export interface FormatSimpleMoneyOptions {
  /** Code only, no symbol — for dense grid cells (e.g. "1.500 CLP"). */
  compact?: boolean;
  onlyNumber?: boolean;
}

/**
 * Money never shows or accepts decimals, for any currency — so every
 * formatter rounds to whole units (`Intl` rounds half-expand, which is fine
 * here).
 *
 * `style: "decimal"` is the only `Intl.NumberFormat` mode reliably supported
 * by Hermes on iOS — `style: "currency"` and `formatToParts` are not, so the
 * symbol and grouping/decimal separators are derived from the explicit
 * tables above instead of asked of `Intl`.
 */
export const buildDecimalFormatter = (locale: string) => {
  return new Intl.NumberFormat(locale, {
    style: "decimal",
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

export const formatSimpleMoney = (
  money: MoneyInput | null | undefined,
  options?: FormatSimpleMoneyOptions,
) => {
  if (!money) return "-";
  if (money.amount === "") return "";

  const code = money.currency;
  const locale = isCurrencyCode(code) ? CURRENCY_LOCALES[code] : "es";
  const numberMoney = Number(money.amount || 0);
  const formatter = buildDecimalFormatter(locale);

  const isNegative = numberMoney < 0;
  const sign = isNegative ? "-" : "";
  const formattedAbsolute = formatter.format(Math.abs(numberMoney));

  if (options?.onlyNumber) {
    return `${sign}${formattedAbsolute}`;
  }

  if (options?.compact) {
    return `${sign}${formattedAbsolute} ${code}`;
  }

  const symbol = isCurrencyCode(code) ? CURRENCY_SYMBOLS[code] : undefined;
  if (!symbol) {
    return `${sign}${formattedAbsolute} ${code}`;
  }

  // "S/" is the only symbol that carries a space in Peruvian convention.
  const symbolSeparator = symbol.endsWith("/") ? " " : "";
  return `${sign}${symbol}${symbolSeparator}${formattedAbsolute} ${code}`;
};

export const formatMoney = (money: MoneyFieldsFragment | null | undefined) => {
  if (!money) return formatSimpleMoney(money);
  return formatSimpleMoney({
    amount: String(money.amount),
    currency: money.currency.code,
  });
};

export type MoneyFragment = MoneyFieldsFragment | null | undefined;

/**
 * Symbol plus grouped number, without the ISO code — for copy where the
 * country is already implied (e.g. "$20.000", "S/ 70").
 */
export const formatMoneySymbolOnly = (money: MoneyFragment): string => {
  if (!money) return formatSimpleMoney(money);
  const code = money.currency.code;
  if (!isCurrencyCode(code)) return formatMoney(money);

  const number = formatSimpleMoney(
    { amount: String(money.amount), currency: code },
    { onlyNumber: true },
  );
  const symbol = CURRENCY_SYMBOLS[code];
  // "S/" is the only symbol that carries a space in Peruvian convention.
  const symbolSeparator = symbol.endsWith("/") ? " " : "";
  return `${symbol}${symbolSeparator}${number}`;
};

export const formatMoneyFromFragment = (moneyFragment?: MoneyFragment) => {
  const money = moneyFragment;
  return formatMoney(money);
};

/**
 * Money for ledger-style rows, where the sign carries meaning — a commission
 * earned vs. one clawed back by a return.
 *
 * Only the positive case gets a prefix: `Intl.NumberFormat` already renders
 * the minus sign, so prepending one by hand yields "- -$1.000".
 */
export const formatSignedMoneyFromFragment = (
  moneyFragment?: MoneyFragment,
) => {
  const formatted = formatMoneyFromFragment(moneyFragment);
  if (!moneyFragment || Number(moneyFragment.amount) < 0) return formatted;
  return `+ ${formatted}`;
};

export const moneyPercentageOfChange = (
  money1fragment: MoneyFragment,
  money2fragment: MoneyFragment,
) => {
  if (!money1fragment || !money2fragment) return 0;
  const money1 = money1fragment;
  const money2 = money2fragment;
  const money1Value = Number(money1.amount);
  const money2Value = Number(money2.amount);
  return (money2Value - money1Value) / money1Value;
};

export const sumMoneyFragments = (moneyFragments: MoneyFragment[]): string => {
  if (!moneyFragments || moneyFragments.length === 0) return "-";

  // Filter out null/undefined fragments and extract money data
  const validMoneyData = moneyFragments
    .filter((fragment): fragment is NonNullable<MoneyFragment> => !!fragment)
    .map((fragment) => fragment);

  if (validMoneyData.length === 0) return "-";

  // Group by currency and sum amounts
  const currencyTotals = validMoneyData.reduce(
    (acc, money) => {
      const currencyCode = money.currency.code;
      if (!acc[currencyCode]) {
        acc[currencyCode] = {
          total: 0,
          currency: money.currency,
        };
      }
      acc[currencyCode].total += Number(money.amount);
      return acc;
    },
    {} as Record<
      string,
      { total: number; currency: { code: string; name: string } }
    >,
  );

  // Format each currency total
  const formattedTotals = Object.values(currencyTotals).map(
    ({ total, currency }) => {
      return formatSimpleMoney({
        amount: String(total),
        currency: currency.code,
      });
    },
  );

  // Join multiple currencies with " + " or return single currency
  return formattedTotals.join(" + ");
};
